import axios from "axios";
import { getToken } from "./auth.api";
import { clearProjetosCache } from "./projetos.api";

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

/* ── Cache em módulo ──────────────────────────────────────────────────
   TTL 30 s: navegação entre Prazos / Acompanhamento / Responsáveis /
   Calendário reutiliza os dados sem re-fetch.
   Operações de escrita limpam o cache para garantir dados frescos.
──────────────────────────────────────────────────────────────────── */
const _cache   = new Map();
const _pending = new Map();
const TTL = 300_000; // 5 min — navegação usa cache; auto-refresh força bust=true

async function cached(key, fn) {
  const hit = _cache.get(key);
  if (hit && Date.now() - hit.ts < TTL) return hit.data;
  if (_pending.has(key)) return _pending.get(key);
  const promise = fn()
    .then(data => { _cache.set(key, { data, ts: Date.now() }); _pending.delete(key); return data; })
    .catch(err  => { _pending.delete(key); throw err; });
  _pending.set(key, promise);
  return promise;
}

export function clearTarefasCache() { _cache.clear(); }

/* ── Leituras (cacheadas) ─────────────────────────────────────────── */

export const getTarefas = (id_ticket, bust = false) => {
  if (bust) _cache.delete(`tarefas_${id_ticket}`);
  return cached(`tarefas_${id_ticket}`, () =>
    api.get("/tarefas", { params: { id_ticket } }).then(r => r.data));
};

export const getComentarios = (id_tarefa) =>
  cached(`coment_${id_tarefa}`, () =>
    api.get(`/tarefas/${id_tarefa}/comentarios`).then(r => r.data));

export const getHistorico = (id_tarefa) =>
  cached(`hist_${id_tarefa}`, () =>
    api.get(`/tarefas/${id_tarefa}/historico`).then(r => r.data));

/* ── Escritas (invalidam cache) ───────────────────────────────────── */

function invalidarTudo() { _cache.clear(); clearProjetosCache(); }

export const criarTarefa = async (data) => {
  const r = await api.post("/tarefas", data);
  invalidarTudo();
  return r.data;
};

export const atualizarTarefa = async (id, data) => {
  const r = await api.put(`/tarefas/${id}`, data);
  invalidarTudo();
  return r.data;
};

export const deletarTarefa = async (id) => {
  const r = await api.delete(`/tarefas/${id}`);
  invalidarTudo();
  return r.data;
};

export const reordenarTarefas = async (items) => {
  const r = await api.put("/tarefas/reordenar", items);
  invalidarTudo();
  return r.data;
};

export const adicionarComentario = async (id_tarefa, autor, comentario) => {
  const r = await api.post(`/tarefas/${id_tarefa}/comentarios`, { autor, comentario });
  _cache.delete(`coment_${id_tarefa}`);
  return r.data;
};

export const deletarComentario = async (id_tarefa, id_comentario) => {
  const r = await api.delete(`/tarefas/${id_tarefa}/comentarios/${id_comentario}`);
  _cache.delete(`coment_${id_tarefa}`);
  return r.data;
};
